import { push } from 'connected-react-router';
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import {
	Item as ProductItem,
	OrderDirections,
	Product,
	ProductsErrorResponse,
	QueryErrors,
	SortField,
	SortFields,
} from '~/Api/Product';
import config from '~/config';
import { fetchCountriesIfNeeded } from '~/Country';
import { fetchCurrenciesIfNeeded as fetchCurrencies, selectCurrency } from '~/Currency';
import { fetchCurrentCustomerDatasourceLicencesIfNeeded } from '~/CustomerDatasourceLicence';
import { handleError } from '~/ErrorHandler';
import { flashSuccessMessage, resetFlashMessages } from '~/FlashMessage';
import { logGoogleTagManagerShareLink } from '~/GoogleTagManager';
import FilterParameter from '~/utils/FilterParameter/FilterParameter';
import {
	loggedFetchMoreProducts,
	PricedProduct,
	pruneProducts,
	Query,
	redirectToSearch,
	selectProductsByQuery,
	selectQueryErrors,
	validateQuery,
} from '~/Product';
import { pruneDatasets } from '~/ProductDataset';
import { pruneGroups } from '~/ProductGroup';
import {
	calculateMissingPricesPercentage,
	fetchPrices,
	pruneAllPrices,
	selectProductPrice,
	State as ProductPriceState,
	updatePriceTypePreference,
} from '~/ProductPrice';
import { pruneAllHistory } from '~/ProductPriceHistory';
import { fetchDataSources } from '~/ProductSearchableDataSource';
import buildProductSearchUrl from '~/Router/buildProductSearchUrl';
import { ApplicationState, ThunkDispatch } from '~/Store';

import SearchPage, { type Props as SearchPageProps } from './SearchPage';
import { FilterOperator } from '~/Api/Product/Query';

const InvalidAtcErrorMessage = 'Please check that you entered ATC correctly';
const InvalidDateErrorMessage =
	'Please check that you entered correct date in correct format, for example: 24. 11. 2017';
const InvalidCountryErrorMessage = 'Please check that you entered country correctly';
const NoSearchParameterErrorMessage = 'Please specify at least one search parameter';

const parseFilterParameters = memoizeOne(
	(filter: any = [], availableParameters): FilterParameter<string | string[]>[] => {
		const filterParameters = (Array.isArray(filter) ? filter : [filter]).map((parameterPair) => {
			const parts = parameterPair.match(/^([^:]+):(.*)$/);
			if (parts === null) {
				return null;
			}

			const parameter = availableParameters.find((parameter: { name: string }) => parameter.name === parts[1]);
			if (!parameter) {
				return null;
			}

			const value = parameter.parseValueFromString(parts[2]);
			if (value === null) {
				return null;
			}
			return new FilterParameter(parameter.name, value);
		});

		return filterParameters.filter((parameter): parameter is FilterParameter<string | string[]> => Boolean(parameter));
	}
);

const createQueryErrors = memoizeOne(
	(
		errors: QueryErrors | null
	): {
		all?: string[];
		atc?: string[];
		country?: string[];
		newerThan?: string[];
	} => {
		if (errors === null) {
			return {};
		}

		const errorMessages: {
			all?: string[];
			atc?: string[];
			country?: string[];
			newerThan?: string[];
		} = {};
		if (errors.all !== undefined) {
			errorMessages.all = [NoSearchParameterErrorMessage];
		}
		if (errors.atc !== undefined) {
			errorMessages.atc = [InvalidAtcErrorMessage];
		}
		if (errors.country !== undefined) {
			errorMessages.country = [InvalidCountryErrorMessage];
		}
		if (errors.newerThan !== undefined) {
			errorMessages.newerThan = [InvalidDateErrorMessage];
		}
		return errorMessages;
	}
);

const selectOrderDirection = memoizeOne((orderDirection: OrderDirections | null) => {
	const direction = orderDirection ? OrderDirections[orderDirection] : undefined;
	return direction !== undefined ? direction : OrderDirections.asc;
});

const memoizedCreatePricedProducts = memoizeOne(
	(
		items: ProductItem[],
		state: ProductPriceState,
		currency: string | null,
		priceType: string | null,
		filterCalculatedPrice: boolean,
		groupId?: string | null
	) => {
		return items.map(
			(item) =>
				new PricedProduct(
					item,
					item.product,
					selectProductPrice(state, item.product, currency, priceType, filterCalculatedPrice, groupId)
				)
		);
	}
);

type OwnProps = Pick<
	SearchPageProps,
	'productId' | 'productTabName' | 'currency' | 'priceType' | 'filterCalculatedPrice'
> & {
	order: OrderDirections | null;
	filter: any;
	filterOperator: FilterOperator | null;
	sort: SortField | null;
	location: {
		search: string;
	};
};

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
	const { productId, productTabName, filterOperator, sort, order, filter } = props;

	const query = new Query(
		parseFilterParameters(filter, state.products.availableParameters),
		filterOperator ? filterOperator : FilterOperator.and,
		(sort ? SortFields[sort] : null) || SortFields.country,
		selectOrderDirection(order)
	);
	const queryErrors = selectQueryErrors(state.products, query);
	const products = selectProductsByQuery(state.products, query);

	const currency = props.currency
		? selectCurrency(state.currency.currencies, props.currency)
			? props.currency
			: config.product.defaultPriceCurrency
		: null;

	let { priceType } = props;
	let pricedProducts =
		products !== null
			? memoizedCreatePricedProducts(
					products.items,
					state.productPrice,
					currency,
					priceType,
					props.filterCalculatedPrice
			  )
			: [];
	const missingPricesPercentage =
		products &&
		priceType === 'ex-factory' &&
		!props.filterCalculatedPrice &&
		state.productPrice.hideTooltipPreference.load() !== '1'
			? calculateMissingPricesPercentage(pricedProducts)
			: 0;

	if (missingPricesPercentage > 90) {
		priceType = null;
		pricedProducts = memoizedCreatePricedProducts(
			products?.items || [],
			state.productPrice,
			currency,
			priceType,
			props.filterCalculatedPrice
		);
	}

	return {
		sortFields: SortFields,
		priceType,
		currency,
		queryErrors: createQueryErrors(queryErrors),
		query,
		products: pricedProducts,
		hasNextPage: products !== null && products.pagination !== null && products.pagination.hasNextPage(),
		isFetchingProducts: products !== null && products.isFetching,
		isFetchingProductPrices: state.productPrice.request !== null,
		pagination: products !== null ? products.pagination : null,
		paginationLimitReached: products?.paginationLimitReached ?? false,
		stats: products?.stats || null,
		productId,
		productTabName,
		missingPricesPercentage,
		isSaving: state.groupProduct.updatingGroupIds.size > 0,
	};
};

const mapDispatchToProps = (dispatch: ThunkDispatch<AnyAction>, props: OwnProps) => {
	return {
		onFetch: async (query: Query<string[]>, priceType: string, filterCalculatedPrice: boolean) => {
			const requiresCountries = query.filterParameters.find((parameter) => parameter.name === 'country') !== undefined;

			try {
				const countries = requiresCountries ? (await dispatch(fetchCountriesIfNeeded()))?.items || [] : [];
				if (!dispatch(validateQuery(query, countries))) {
					return;
				}

				const currencyCode = props.currency;
				if (currencyCode && !selectCurrency(await dispatch(fetchCurrencies()), currencyCode)) {
					return;
				}

				dispatch(fetchAccessTokenIfNeeded()).catch((response) => dispatch(handleError(response)));
				dispatch(fetchCurrentCustomerDatasourceLicencesIfNeeded());

				dispatch(loggedFetchMoreProducts(query))
					.then((response) => {
						if (
							currencyCode !== config.product.defaultPriceCurrency &&
							response !== null &&
							response.items.length > 0
						) {
							return dispatch(
								fetchPrices(
									response.items.map((item) => item.product.id),
									currencyCode
								)
							);
						}
						return null;
					})
					.catch((response) => {
						if (!(response instanceof ProductsErrorResponse)) {
							dispatch(handleError(response));
							return undefined;
						}
						if (response.handleSearchInvalidDatasourceCondition()) {
							dispatch(fetchDataSources())
								.then((response) =>
									handleInvalidDataSourceTypes(
										dispatch,
										null,
										response.items,
										query,
										currencyCode,
										priceType,
										filterCalculatedPrice
									)
								)
								.catch((response) => dispatch(handleError(response)));
						}
						dispatch(handleError(response));
					});
			} catch (error) {
				dispatch(handleError(error));
			}
		},
		onFetchPrices: async (currency: string, productIds: string[]) => {
			try {
				if (currency !== config.product.defaultPriceCurrency) {
					await dispatch(fetchPrices(productIds, currency));
				}
				dispatch(flashSuccessMessage('Products currency has been changed'));
			} catch (error) {
				dispatch(handleError(error));
			}
		},
		onPrune: (query: Query) => {
			if (query) {
				dispatch(pruneProducts(query));
			}
			dispatch(pruneAllPrices());
			dispatch(pruneAllHistory());
			dispatch(pruneGroups());
			dispatch(pruneDatasets());
		},
		onSearch: (
			query: Query,
			productId: string | null,
			priceType: string | null,
			currentPriceType: string | null,
			currency: string | null,
			filterCalculatedPrice: boolean
		) => {
			dispatch(redirectToSearch(query, productId, priceType, currency, filterCalculatedPrice));
			if (currentPriceType !== priceType) {
				dispatch(updatePriceTypePreference(priceType, filterCalculatedPrice));
				dispatch(flashSuccessMessage('Products price type has been changed'));
			}
		},
		searchLink: (
			query: Query,
			currency: string | null,
			priceType: string | null,
			filterCalculatedPrice: boolean,
			productId: string | null
		) =>
			dispatch((dispatch, getState) =>
				buildProductSearchUrl(
					query,
					productId,
					priceType !== undefined ? priceType : getState().productPrice.priceTypePreference.load(),
					currency,
					filterCalculatedPrice
				)
			),
		productDetailLink: (product: Product) => ({
			pathname: `/search/${encodeURIComponent(product.id)}`,
			search: props.location.search,
		}),
		onCloseDetail: () =>
			dispatch(
				push({
					pathname: '/search/',
					search: props.location.search,
				})
			),
		onShowDetail: (productId: string, tabName: string) =>
			dispatch(
				push({
					pathname: `/search/${encodeURIComponent(productId)}${
						tabName !== 'info' ? `/${encodeURIComponent(tabName)}` : ''
					}`,
					search: props.location.search,
				})
			),
		onShare: () => logGoogleTagManagerShareLink('search', window.location.href),
		onProductSelected: () => dispatch(resetFlashMessages()),
	};
};

function handleInvalidDataSourceTypes(
	dispatch: ThunkDispatch<AnyAction>,
	productId: string | null,
	dataSources: { type: string }[],
	query: Query<string[]>,
	currency: string | null,
	priceType: string,
	filterCalculatedPrice?: boolean
) {
	const dataSourceTypes = new Set(dataSources.map((dataSource) => dataSource.type));
	const newFilterParameters = query.filterParameters.map((filterParameter) => {
		if (filterParameter.name !== 'datasource') {
			return filterParameter;
		}

		const types = filterParameter.value.filter((type) => dataSourceTypes.has(type));
		return filterParameter.valueUpdated(types);
	});
	dispatch(
		redirectToSearch(
			query.filterParametersUpdated(newFilterParameters),
			null,
			priceType,
			currency,
			Boolean(filterCalculatedPrice)
		)
	);
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchPage);
